import api, { handleApiError } from './api';
import { Post, Comment, PostCreate, CommentCreate, PostShareCreate } from '../types/social';

interface GetMessagesOptions {
  skip?: number;
  limit?: number;
  beforeMessageId?: number;
  afterMessageId?: number;
}

const POST_VIEW_BATCH_DELAY_MS = 1_000;
const POST_VIEW_MAX_BATCH_SIZE = 25;
const CONVERSATION_CACHE_MS = 10_000;
const UNREAD_MESSAGE_COUNT_CACHE_MS = 10_000;
const pendingPostViews = new Set<number>();
let postViewFlushTimer: ReturnType<typeof setTimeout> | null = null;
let postViewFlushInFlight: Promise<void> | null = null;
let postViewFlushEventsRegistered = false;
let conversationsCache:
  | { key: string; timestamp: number; data: any[] }
  | null = null;
let conversationsInFlight:
  | { key: string; promise: Promise<any[]> }
  | null = null;
let unreadMessageCountCache:
  | { timestamp: number; count: number }
  | null = null;
let unreadMessageCountInFlight: Promise<number> | null = null;
let conversationCacheGeneration = 0;

const clearConversationCaches = () => {
  conversationCacheGeneration += 1;
  conversationsCache = null;
  conversationsInFlight = null;
  unreadMessageCountCache = null;
  unreadMessageCountInFlight = null;
};

const flushPostViews = async (): Promise<void> => {
  if (postViewFlushTimer) {
    clearTimeout(postViewFlushTimer);
    postViewFlushTimer = null;
  }

  if (postViewFlushInFlight) {
    postViewFlushInFlight.finally(() => {
      if (pendingPostViews.size > 0) {
        schedulePostViewFlush();
      }
    });
    return postViewFlushInFlight;
  }

  const postIds = Array.from(pendingPostViews);
  pendingPostViews.clear();
  if (postIds.length === 0) return;

  postViewFlushInFlight = api.post('/news-feed/posts/views', { post_ids: postIds })
    .catch(async () => {
      await Promise.all(
        postIds.map((postId) => api.post(`/news-feed/posts/${postId}/view`).catch(() => undefined))
      );
    })
    .then(() => undefined)
    .finally(() => {
      postViewFlushInFlight = null;
    });

  return postViewFlushInFlight;
};

const schedulePostViewFlush = () => {
  if (postViewFlushTimer) return;
  postViewFlushTimer = setTimeout(() => {
    flushPostViews().catch(() => undefined);
  }, POST_VIEW_BATCH_DELAY_MS);
};

const registerPostViewFlushEvents = () => {
  if (postViewFlushEventsRegistered || typeof window === 'undefined') return;
  postViewFlushEventsRegistered = true;

  const flush = () => {
    flushPostViews().catch(() => undefined);
  };

  window.addEventListener('pagehide', flush);
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') flush();
  });
};

export const socialService = {
  // Posts — paginated feed (does NOT load all posts at once)
  async getFeed(
    feedType: 'all' | 'following' | 'trending' | 'foryou' = 'all',
    skip = 0,
    limit = 20,
    location?: string
  ): Promise<Post[]> {
    // Add _t to bust any browser/axios cache so every call returns fresh
    // ranked results from the backend.
    const cacheBust = Date.now();
    let url = `/news-feed/posts?feed_type=${feedType}&skip=${skip}&limit=${limit}&_t=${cacheBust}`;
    if (location) url += `&location=${encodeURIComponent(location)}`;
    const response = await api.get(url);
    return response.data;
  },


  // Search posts — @username, #hashtag, text search (Twitter/Instagram style)
  async searchPosts(
    query: string,
    skip = 0,
    limit = 20,
    searchType: 'all' | 'content' | 'tags' | 'location' | 'user' = 'all'
  ): Promise<Post[]> {
    const response = await api.get(
      `/news-feed/search?q=${encodeURIComponent(query)}&skip=${skip}&limit=${limit}&search_type=${searchType}`
    );
    return response.data;
  },

  // Search users (for @ mention autocomplete)
  async searchUsers(query: string, limit = 10): Promise<any[]> {
    const cleanQuery = query.replace(/^@/, '');
    if (!cleanQuery) return [];
    const response = await api.get(`/search/users?username=${encodeURIComponent(cleanQuery)}&limit=${limit}`);
    return response.data;
  },

  async getPost(postId: number): Promise<Post> {
    const response = await api.get(`/news-feed/posts/${postId}`);
    return response.data;
  },

  async createPost(postData: PostCreate): Promise<Post> {
    try {
      const response = await api.post('/news-feed/posts', postData);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async updatePost(postId: number, postData: Partial<PostCreate>): Promise<Post> {
    const response = await api.put(`/news-feed/posts/${postId}`, postData);
    return response.data;
  },

  async deletePost(postId: number): Promise<void> {
    await api.delete(`/news-feed/posts/${postId}`);
  },

  // Likes
  async likePost(postId: number, reaction: string = 'like'): Promise<any> {
    const response = await api.post(`/news-feed/posts/${postId}/like?reaction=${encodeURIComponent(reaction)}`);
    return response.data;
  },

  async unlikePost(postId: number): Promise<void> {
    await api.delete(`/news-feed/posts/${postId}/like`);
  },

  // Comments
  async addComment(postId: number, commentData: CommentCreate): Promise<Comment> {
    const response = await api.post(`/news-feed/posts/${postId}/comments`, commentData);
    return response.data;
  },

  async likeComment(commentId: number): Promise<any> {
    const response = await api.post(`/news-feed/comments/${commentId}/like`);
    return response.data;
  },

  async unlikeComment(commentId: number): Promise<void> {
    await api.delete(`/news-feed/comments/${commentId}/like`);
  },

  async replyToComment(commentId: number, commentData: CommentCreate): Promise<Comment> {
    const response = await api.post(`/news-feed/comments/${commentId}/replies`, commentData);
    return response.data;
  },

  async getCommentReplies(commentId: number): Promise<Comment[]> {
    const response = await api.get(`/news-feed/comments/${commentId}/replies`);
    return response.data;
  },

  async editComment(commentId: number, content: string): Promise<Comment> {
    const response = await api.put(`/news-feed/comments/${commentId}`, { content });
    return response.data;
  },

  async deleteComment(commentId: number): Promise<void> {
    await api.delete(`/news-feed/comments/${commentId}`);
  },

  // Shares
  async sharePost(postId: number, shareData: PostShareCreate): Promise<any> {
    const response = await api.post(`/news-feed/posts/${postId}/share`, shareData);
    return response.data;
  },

  // View tracking — fire-and-forget when post enters viewport
  async recordView(postId: number): Promise<void> {
    registerPostViewFlushEvents();
    pendingPostViews.add(postId);

    try {
      if (pendingPostViews.size >= POST_VIEW_MAX_BATCH_SIZE) {
        await flushPostViews();
      } else {
        schedulePostViewFlush();
      }
    } catch {
      // intentionally silent — view tracking should never block UX
    }
  },

  // Media upload — optional onProgress callback for background upload progress bar
  async uploadMedia(formData: FormData, onProgress?: (pct: number) => void): Promise<{
    url: string;
    thumbnail_url?: string;
    media_type?: string;
    conversion_id?: string;
    hls_status?: string;
    width?: number;
    height?: number;
    duration?: number;
    file_size?: number;
  }> {
    try {
      const response = await api.post('/media/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        onUploadProgress: onProgress
          ? (evt) => { if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100)); }
          : undefined,
      });
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // HLS conversion status check
  async checkHlsStatus(conversionId: string): Promise<{
    status: string;
    hls_url?: string;
    thumbnail_url?: string;
    duration?: number;
    qualities?: string[];
    width?: number;
    height?: number;
    file_size?: number;
  }> {
    try {
      const response = await api.get(`/media/hls-status/${conversionId}`);
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  // Direct Messages
  clearConversationCache(): void {
    clearConversationCaches();
  },

  async getUnreadMessageCount(forceRefresh = false): Promise<number> {
    const now = Date.now();

    if (!forceRefresh && unreadMessageCountCache && now - unreadMessageCountCache.timestamp < UNREAD_MESSAGE_COUNT_CACHE_MS) {
      return unreadMessageCountCache.count;
    }

    if (!forceRefresh && unreadMessageCountInFlight) {
      return unreadMessageCountInFlight;
    }

    const cacheGeneration = conversationCacheGeneration;
    unreadMessageCountInFlight = api.get('/messages/unread-count')
      .then((response) => {
        const count = Number(response.data?.count || 0);
        if (cacheGeneration === conversationCacheGeneration) {
          unreadMessageCountCache = { timestamp: Date.now(), count };
        }
        return count;
      })
      .finally(() => {
        if (cacheGeneration === conversationCacheGeneration) {
          unreadMessageCountInFlight = null;
        }
      });

    return unreadMessageCountInFlight;
  },

  async getConversations(skip = 0, limit = 50, forceRefresh = false): Promise<any[]> {
    const key = `${skip}:${limit}`;
    const now = Date.now();

    if (!forceRefresh && conversationsInFlight?.key === key) {
      return conversationsInFlight.promise;
    }

    if (!forceRefresh && conversationsCache?.key === key && now - conversationsCache.timestamp < CONVERSATION_CACHE_MS) {
      return conversationsCache.data;
    }

    const cacheGeneration = conversationCacheGeneration;
    const promise = api.get(`/messages/conversations?skip=${skip}&limit=${limit}`)
      .then((response) => {
        if (cacheGeneration === conversationCacheGeneration) {
          conversationsCache = { key, timestamp: Date.now(), data: response.data };
        }
        return response.data;
      })
      .finally(() => {
        if (cacheGeneration === conversationCacheGeneration && conversationsInFlight?.key === key) {
          conversationsInFlight = null;
        }
      });

    conversationsInFlight = { key, promise };
    return promise;
  },

  async getMessages(conversationId: number, options: GetMessagesOptions = {}): Promise<any[]> {
    const {
      skip = 0,
      limit = 50,
      beforeMessageId,
      afterMessageId,
    } = options;
    const params = new URLSearchParams({
      skip: String(skip),
      limit: String(limit),
    });
    if (beforeMessageId && beforeMessageId > 0) params.set('before_message_id', String(beforeMessageId));
    if (afterMessageId && afterMessageId > 0) params.set('after_message_id', String(afterMessageId));
    const response = await api.get(`/messages/conversations/${conversationId}/messages?${params.toString()}`);
    return response.data;
  },

  async sendMessage(recipientId: number, content: string, clientMessageId?: string, replyToId?: number): Promise<any> {
    const response = await api.post('/messages/send', {
      recipient_id: recipientId,
      content,
      client_message_id: clientMessageId,
      reply_to_id: replyToId,
    });
    clearConversationCaches();
    return response.data;
  },

  async sendMediaMessage(
    recipientId: number,
    file: File,
    content?: string,
    clientMessageId?: string,
    replyToId?: number,
    onProgress?: (pct: number) => void,
  ): Promise<any> {
    const formData = new FormData();
    formData.append('recipient_id', recipientId.toString());
    formData.append('file', file);
    if (content) {
      formData.append('content', content);
    }
    if (clientMessageId) {
      formData.append('client_message_id', clientMessageId);
    }
    if (replyToId) {
      formData.append('reply_to_id', replyToId.toString());
    }
    try {
      const response = await api.post('/messages/send-media', formData, {
        headers: {
          'Content-Type': 'multipart/form-data'
        },
        onUploadProgress: onProgress
          ? (evt) => { if (evt.total) onProgress(Math.round((evt.loaded / evt.total) * 100)); }
          : undefined,
      });
      clearConversationCaches();
      return response.data;
    } catch (error) {
      throw new Error(handleApiError(error));
    }
  },

  async markAsRead(conversationId: number): Promise<void> {
    await api.post(`/messages/conversations/${conversationId}/read`);
    clearConversationCaches();
  },

  async deleteConversation(conversationId: number): Promise<void> {
    await api.delete(`/messages/conversations/${conversationId}`);
    clearConversationCaches();
  },

  async deleteMessage(messageId: number, forEveryone: boolean): Promise<void> {
    await api.delete(`/messages/messages/${messageId}?for_everyone=${forEveryone}`);
    clearConversationCaches();
  },

  async reactToMessage(messageId: number, reaction: string): Promise<any> {
    const response = await api.post(`/messages/messages/${messageId}/reactions`, { reaction });
    return response.data;
  },

  async removeMessageReaction(messageId: number): Promise<any> {
    const response = await api.delete(`/messages/messages/${messageId}/reactions`);
    return response.data;
  },

  async blockUser(userId: number): Promise<any> {
    const response = await api.post(`/messages/block/${userId}`);
    clearConversationCaches();
    return response.data;
  },

  async unblockUser(userId: number): Promise<any> {
    const response = await api.post(`/messages/unblock/${userId}`);
    clearConversationCaches();
    return response.data;
  },

  async getBlockedUsers(): Promise<{ id: number; username: string; avatar_url: string | null; is_verified: boolean }[]> {
    const response = await api.get('/messages/blocked');
    return response.data;
  },

  async reportUser(userId: number, reason: string, details?: string): Promise<any> {
    const formData = new FormData();
    formData.append('reason', reason);
    if (details) {
      formData.append('details', details);
    }
    const response = await api.post(`/messages/report/${userId}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  },

  // Online presence heartbeat
  async sendHeartbeat(): Promise<void> {
    try {
      await api.post('/messages/heartbeat');
    } catch {
      // Silent fail — heartbeat should never block UX
    }
  }
};
